import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { T, SERIF, alpha } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';
import { useTheme } from './theme-context';
import { MemorySavedNote, ActionCard, SuggestionList, useSuggestions, ActionCardData, MemorySaved, Suggestion, useMediaAttach, MediaChips, AttachButton } from './AssistantMemory';

addTranslations({
  'Bugün': 'Today',
  'Dün': 'Yesterday',
  'Yeni Sohbet': 'New Chat',
  'Geçmiş': 'History',
  'Henüz sohbet yok.': 'No conversations yet.',
  'Yanıtlanıyor…': 'Answering…',
  'Hoş geldin': 'Welcome',
  'Kampanyaların, metriklerin veya reklam stratejin hakkında istediğini sor.': 'Ask anything about your campaigns, metrics, or ad strategy.',
  'Poyais One canlı verilerle yanıtlar.': 'Poyais One answers with live data.',
  'Kampanyaların hakkında bir şey sor…': 'Ask something about your campaigns…',
  'Beceriler için': 'For skills, type',
  'Haftalık rapor': 'Weekly report',
  'Bu haftanın performans özetini ver': "Give me this week's performance summary",
  'En iyi performans': 'Top performers',
  'Bu dönemde en iyi performans gösteren kampanyalar hangileri?': 'Which campaigns are performing best in this period?',
  'Bütçe kaydırma': 'Budget shift',
  'Bütçemi kanallar arasında nasıl dağıtmalıyım?': 'How should I distribute my budget across channels?',
  'Yanıt hazırlanıyor…': 'Preparing an answer…',
  'Bir hata oluştu, tekrar dener misin?': 'Something went wrong, could you try again?',
  'Bu özellik mevcut planınızda yok.': 'This feature is not available on your current plan.',
  'Dönem': 'Period',
  'Başka bir şey sor…': 'Ask something else…',
  'Kullanıcı': 'User',
  'Yeniden adlandır': 'Rename',
  'Sil': 'Delete',
  'Bu sohbeti silmek istediğine emin misin?': 'Are you sure you want to delete this conversation?',
});

const API = '/api/v1';
const HISTORY_LIMIT = 40;

function historyKey() {
  const u = JSON.parse(localStorage.getItem('poyais_user') || '{}');
  const clientId = localStorage.getItem('poyais_active_client') || '1';
  return `poyais_asistan_history_${u?.id || 'anon'}_${clientId}`;
}

function sessionDateLabel(id: string, lang: 'tr'|'en' = 'tr') {
  const ts = Number(id.slice(1));
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return lang === 'en' ? 'Today' : 'Bugün';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return lang === 'en' ? 'Yesterday' : 'Dün';
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { day: '2-digit', month: 'short' });
}

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const monthStartStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };

type ChipDef = { label: string; question: string };
const CHIPS_TR: ChipDef[] = [
  { label: 'Haftalık rapor', question: 'Bu haftanın performans özetini ver' },
  { label: 'En iyi performans', question: 'Bu dönemde en iyi performans gösteren kampanyalar hangileri?' },
  { label: 'Bütçe kaydırma', question: 'Bütçemi kanallar arasında nasıl dağıtmalıyım?' },
];
const CHIPS_EN: ChipDef[] = [
  { label: 'Weekly report', question: "Give me this week's performance summary" },
  { label: 'Top performers', question: 'Which campaigns are performing best in this period?' },
  { label: 'Budget shift', question: 'How should I distribute my budget across channels?' },
];

type Turn = {
  question: string; loading: boolean; error?: boolean; errorMsg?: string;
  answer?: string; context_period?: string;
  memorySaved?: MemorySaved[]; actions?: ActionCardData[];
};
type Session = { id: string; title: string; turns: Turn[] };

// Eski (tek soru/cevaplık) oturum kaydı biçimi — geçmiş localStorage verisini göçürmek için.
type LegacySession = { id: string; question: string; loading?: boolean; error?: boolean; errorMsg?: string; answer?: string; context_period?: string };

function migrateSessions(raw: any[]): Session[] {
  return raw.map((s: any) => {
    if (Array.isArray(s.turns)) return { ...s, turns: s.turns.map((t: Turn) => ({ ...t, loading: false })) };
    const legacy = s as LegacySession;
    return {
      id: legacy.id,
      title: legacy.question,
      turns: [{ question: legacy.question, loading: false, error: legacy.error, errorMsg: legacy.errorMsg, answer: legacy.answer, context_period: legacy.context_period }],
    };
  });
}

export default function Asistan({ config }: { config: any }) {
  const { t, lang } = useLang();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const CHIPS = lang==='en' ? CHIPS_EN : CHIPS_TR;
  const [sessions, setSessions] = useState<Session[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(historyKey()) || '[]');
      return Array.isArray(saved) ? migrateSessions(saved) : [];
    } catch { return []; }
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const clientKey = localStorage.getItem('poyais_active_client') || '1';
  const attach = useMediaAttach(clientKey);
  // Tarih seçici kaldırıldı: asistan her zaman bu ayı (ay başı → bugün) esas alır; dönem yanıtın altında yazar.
  const range = { start: monthStartStr(), end: todayStr() };
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [histOpen, setHistOpen] = useState(false);   // mobilde sohbet geçmişi varsayılan kapalı (ekranı kaplamasın)
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(historyKey(), JSON.stringify(sessions.slice(0, HISTORY_LIMIT)));
  }, [sessions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuFor(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeId, sessions]);

  const gadsId = config?.platforms?.gads?.customerId || '';
  const metaId = config?.platforms?.meta?.accountId || '';
  const ga4Id = config?.platforms?.ga4?.propertyId || '';

  const active = sessions.find(s => s.id === activeId) || null;
  const suggestions = useSuggestions(localStorage.getItem('poyais_active_client') || '1');

  // Sohbetin sunucu kopyası: asistanın "geçen hafta ne konuşmuştuk?" sorularını yanıtlayabilmesi için
  // (yalnızca kendi sohbetlerin; ekip arkadaşların göremez). Sessizce başarısız olabilir.
  const pushChat = (id: string, title: string, turns: Turn[]) => {
    const done = turns.filter(tn => !tn.loading && !tn.error && tn.answer).map(tn => ({ question: tn.question, answer: tn.answer }));
    if (!done.length) return;
    axios.post(`${API}/ai-advisor/chats`, { id, title, turns: done, client_id: Number(localStorage.getItem('poyais_active_client') || '1') }).catch(() => {});
  };
  const updateAction = (sessionId: string, turnIdx: number, card: ActionCardData) => {
    setSessions(ss => ss.map(s => s.id === sessionId ? {
      ...s, turns: s.turns.map((tn, i) => i === turnIdx ? { ...tn, actions: (tn.actions || []).map(a => a.id === card.id ? card : a) } : tn),
    } : s));
  };
  const pickSuggestion = (sg: Suggestion) => { if (sg.mode === 'fill') setInput(sg.question); else ask(sg.question); };

  const ask = async (question: string) => {
    if (!question.trim()) return;
    const q = question.trim() + attach.suffix();   // eklenen medya kimlikleriyle birlikte gider (asistan list_media ile okur)
    attach.clear();
    setInput('');

    let sessionId = activeId;
    const priorTurns = active ? active.turns.filter(tn => !tn.loading && !tn.error && tn.answer) : [];

    if (sessionId && active) {
      setSessions(ss => ss.map(s => s.id === sessionId ? { ...s, turns: [...s.turns, { question: q, loading: true }] } : s));
    } else {
      sessionId = 's' + Date.now();
      setSessions(ss => [{ id: sessionId!, title: q, turns: [{ question: q, loading: true }] }, ...ss]);
      setActiveId(sessionId);
    }

    const turnIndex = (priorTurns.length);
    try {
      const clientId = localStorage.getItem('poyais_active_client') || '1';
      const history = priorTurns.map(tn => ({ question: tn.question, answer: tn.answer || '' }));
      const res = await axios.post(`${API}/ai-advisor/ask`, {
        question: q, ga4_id: ga4Id, gads_id: gadsId, meta_id: metaId,
        start_date: range.start, end_date: range.end, client_id: Number(clientId), history, chat_id: sessionId,
      });
      const newTurn: Turn = { question: q, loading: false, answer: res.data?.answer, context_period: res.data?.context_period,
        memorySaved: res.data?.memory_saved, actions: res.data?.actions };
      setSessions(ss => ss.map(s => s.id === sessionId ? {
        ...s, turns: s.turns.map((tn, i) => i === turnIndex ? { ...tn, ...newTurn } : tn),
      } : s));
      pushChat(sessionId!, active?.title || q, [...priorTurns, newTurn]);
    } catch (e: any) {
      const msg = e?.response?.status === 403 ? (e.response?.data?.detail || t('Bu özellik mevcut planınızda yok.')) : undefined;
      setSessions(ss => ss.map(s => s.id === sessionId ? {
        ...s, turns: s.turns.map((tn, i) => i === turnIndex ? { ...tn, loading: false, error: true, errorMsg: msg } : tn),
      } : s));
    }
  };

  const deleteSession = (id: string) => {
    if (!window.confirm(t('Bu sohbeti silmek istediğine emin misin?'))) return;
    setSessions(ss => ss.filter(s => s.id !== id));
    axios.delete(`${API}/ai-advisor/chats/${encodeURIComponent(id)}?client_id=${localStorage.getItem('poyais_active_client') || '1'}`).catch(() => {});
    if (activeId === id) setActiveId(null);
    setMenuFor(null);
  };

  const startRename = (s: Session) => {
    setRenaming(s.id);
    setRenameVal(s.title);
    setMenuFor(null);
  };

  const commitRename = (id: string) => {
    const v = renameVal.trim();
    if (v) {
      setSessions(ss => ss.map(s => s.id === id ? { ...s, title: v } : s));
      const sess = sessions.find(s => s.id === id);
      if (sess) pushChat(id, v, sess.turns);
    }
    setRenaming(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: 'calc(var(--vh, 1vh) * 100 - 96px)', minHeight: isMobile ? 420 : 520 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' as const : 'row' as const,
        gap: isMobile ? 8 : 18, alignItems: 'stretch', flex: 1, minHeight: 0,
        overflowY: isMobile ? 'auto' as const : 'visible' as const }}>
        {/* history rail */}
        <div style={{ flex: isMobile ? '0 0 auto' : '0 1 260px', minWidth: isMobile ? 'auto' : 220,
          maxHeight: isMobile ? (histOpen ? 280 : 'none') : 'none', overflowY: isMobile ? 'auto' as const : 'visible' as const,
          border: `1px solid ${T.border}`, borderRadius: isMobile ? 14 : 16, background: T.surface, padding: isMobile ? 8 : 14 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: isMobile ? (histOpen ? 8 : 0) : 14 }}>
            <button onClick={() => { setActiveId(null); setHistOpen(false); }} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 9,
              border: `1px solid ${T.borderMid}`, background: T.surface, borderRadius: 11, padding: isMobile ? '9px 12px' : '11px 13px', fontFamily: 'inherit',
              fontSize: 13, fontWeight: 700, color: T.text, cursor: 'pointer' }}>
              {t('Yeni Sohbet')}
            </button>
            {isMobile && sessions.length > 0 && (
              <button onClick={() => setHistOpen(o => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${T.borderMid}`,
                background: histOpen ? alpha(T.accent, 12) : T.surface, borderRadius: 11, padding: '9px 12px', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                color: T.text, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('Geçmiş')} ({sessions.length})
                <span style={{ display: 'flex', transform: histOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><Icon.chevron size={13} /></span>
              </button>
            )}
          </div>
          {(!isMobile || histOpen) && <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {sessions.length === 0 && !isMobile && <div style={{ fontSize: 12, color: T.textDim, padding: '11px 12px' }}>{t('Henüz sohbet yok.')}</div>}
            {sessions.map(s => (
              <div key={s.id} style={{ position: 'relative' }}>
                {renaming === s.id ? (
                  <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                    onBlur={() => commitRename(s.id)}
                    onKeyDown={e => { if (e.key === 'Enter') commitRename(s.id); if (e.key === 'Escape') setRenaming(null); }}
                    style={{ width: '100%', boxSizing: 'border-box' as const, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                      border: `1px solid ${T.accent}`, borderRadius: 10, padding: '10px 11px', color: T.text, outline: 'none' }} />
                ) : (
                  <div onClick={() => { setActiveId(s.id); setHistOpen(false); }} style={{ padding: '11px 34px 11px 12px', borderRadius: 10, cursor: 'pointer',
                    background: s.id === activeId ? alpha(T.accent, 12) : 'transparent' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: s.id === activeId ? 'var(--py-amber-deep-text)' : T.text, marginBottom: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{s.title}</div>
                    <div style={{ fontSize: 11, color: s.id === activeId ? T.accentDeep : 'var(--py-text-mid-alt)' }}>
                      {s.turns[s.turns.length - 1]?.loading ? t('Yanıtlanıyor…') : sessionDateLabel(s.id, lang)}
                    </div>
                  </div>
                )}
                {renaming !== s.id && (
                  <button onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === s.id ? null : s.id); }}
                    style={{ position: 'absolute', top: 8, right: 4, width: 24, height: 24, border: 'none', background: 'transparent',
                      color: T.textDim, cursor: 'pointer', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon.moreHorizontal size={15} />
                  </button>
                )}
                {menuFor === s.id && (
                  <div ref={menuRef} style={{ position: 'absolute', top: 30, right: 4, zIndex: 20, background: T.surface,
                    border: `1px solid ${T.borderMid}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(20,22,43,.12)', minWidth: 140, overflow: 'hidden' }}>
                    <div onClick={() => startRename(s)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', fontSize: 12.5, color: T.text, cursor: 'pointer' }}>
                      <Icon.pencil size={13} sw={1.9} />{t('Yeniden adlandır')}
                    </div>
                    <div onClick={() => deleteSession(s.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', fontSize: 12.5, color: T.redText, cursor: 'pointer', borderTop: `1px solid ${T.border}` }}>
                      <Icon.trash size={13} sw={1.9} />{t('Sil')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>}
        </div>

        {/* main panel */}
        {!active ? (
          <div style={{ flex: isMobile ? '1 1 0' : '1 1 460px', minWidth: 0, minHeight: 0, border: `1px solid ${T.border}`, borderRadius: isMobile ? 14 : 16, background: T.surface,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: isMobile ? '12px 14px' : '24px 28px', overflowY: 'auto' }}>
            {/* üst/alt esnek boşluk: içerik kısaysa dikey ortalar, uzunsa küçülüp panel kaydırılır (taşma yok) */}
            <div style={{ flex: isMobile ? '1 0 4px' : '1 0 28px' }} />
            <img src={theme === 'dark' ? '/poyaisone-logo-nav-dark.png' : '/poyaisone_logo_black.png'} alt="Poyais One"
              style={{ height: isMobile ? 46 : 60, width: 'auto', objectFit: 'contain', marginBottom: isMobile ? 8 : 14 }} />
            <h2 style={{ margin: 0, fontFamily: SERIF, fontSize: isMobile ? 24 : 30, fontWeight: 400, letterSpacing: '-0.01em', color: T.text }}>{t('Hoş geldin')}, {config?.profile?.name || t('Kullanıcı')}</h2>
            <p style={{ margin: isMobile ? '4px 0 10px' : '6px 0 16px', fontSize: isMobile ? 12.5 : 13.5, lineHeight: isMobile ? 1.45 : 1.6, color: 'var(--py-text-mid-alt)', textAlign: 'center', maxWidth: '100%' }}>
              <span style={{ display: isMobile ? 'inline' : 'block', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{t('Kampanyaların, metriklerin veya reklam stratejin hakkında istediğini sor.')}</span>
              {isMobile && ' '}<span style={{ display: isMobile ? 'inline' : 'block' }}>{t('Poyais One canlı verilerle yanıtlar.')}</span>
            </p>
            <div style={{ width: '100%', maxWidth: 640, border: `1px solid ${T.borderMid}`, borderRadius: 16, background: T.surface,
              boxShadow: '0 1px 2px rgba(20,22,43,.05), 0 12px 28px rgba(20,22,43,.05)', padding: isMobile ? '9px 12px' : '12px 16px' }}>
              <MediaChips attach={attach} clientId={clientKey} />
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask(input)}
                placeholder={t('Kampanyaların hakkında bir şey sor…')}
                style={{ border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, color: T.text, width: '100%', marginBottom: isMobile ? 8 : 14, background: 'transparent' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AttachButton attach={attach} size={34} />
                <Icon.search size={18} color="var(--py-text-mid-alt)" sw={1.9} />
                <span style={{ fontSize: 12, color: T.textFaint }}>{t('Beceriler için')} <strong style={{ color: 'var(--py-text-mid-alt)' }}>/</strong></span>
                <div style={{ flex: 1 }} />
                <button onClick={() => ask(input)} disabled={!input.trim()} style={{ width: 34, height: 34, borderRadius: '50%', border: 'none',
                  background: T.accent, color:'#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: input.trim() ? 'pointer' : 'default', opacity: input.trim() ? 1 : 0.5 }}>
                  <Icon.send size={17} />
                </button>
              </div>
            </div>
            {/* mobilde 3 düğme tek satırda eşit genişlikte; masaüstünde eskisi gibi */}
            <div style={isMobile
              ? { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, width: '100%', marginTop: 10 }
              : { display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 14 }}>
              {CHIPS.map(c => (
                <span key={c.label} onClick={() => ask(c.question)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  border: `1px solid ${T.borderMid}`, borderRadius: 999, padding: isMobile ? '8px 4px' : '9px 16px', fontSize: isMobile ? 11.5 : 12.5, fontWeight: 600,
                  color: T.textMid, cursor: 'pointer', background: T.surface, textAlign: 'center', lineHeight: 1.2, minWidth: 0 }}>
                  {!isMobile && <Icon.chevronRight size={14} color="var(--py-amber-deep-text)" sw={2} />}{t(c.label)}
                </span>
              ))}
            </div>
            <SuggestionList items={suggestions.slice(0, isMobile ? 1 : 2)} onPick={pickSuggestion} compact />
            <div style={{ flex: isMobile ? '1 0 2px' : '1 0 8px' }} />
          </div>
        ) : (
          <div style={{ flex: isMobile ? '1 1 0' : '1 1 460px', minWidth: 0, minHeight: 0, border: `1px solid ${T.border}`, borderRadius: isMobile ? 14 : 16, background: T.surface,
            display: 'flex', flexDirection: 'column' }}>
            <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '12px 12px' : '22px 24px', display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20 }}>
              {active.turns.map((tn, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 16 }}>
                  <div style={{ alignSelf: 'flex-end', maxWidth: isMobile ? '90%' : '80%', background: T.bg, borderRadius: 14, padding: isMobile ? '9px 12px' : '12px 16px', fontSize: 13.5, color: T.text, whiteSpace: 'pre-wrap' as const }}>
                    {tn.question}
                  </div>
                  <div style={{ display: 'flex', gap: isMobile ? 8 : 12, alignItems: 'flex-start' }}>
                    <div style={{ width: isMobile ? 26 : 30, height: isMobile ? 26 : 30, borderRadius: 10, background: alpha(T.accent, 14),
                      color: 'var(--py-amber-deep-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon.sparkle size={15} sw={1.9} />
                    </div>
                    {tn.loading ? (
                      <div style={{ fontSize: 13.5, color: 'var(--py-text-mid-alt)' }}>{t('Yanıt hazırlanıyor…')}</div>
                    ) : tn.error ? (
                      <div style={{ fontSize: 13.5, color: T.redText }}>{tn.errorMsg || t('Bir hata oluştu, tekrar dener misin?')}</div>
                    ) : (
                      <div style={{ maxWidth: isMobile ? 'calc(100% - 34px)' : '80%', minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: T.text, lineHeight: isMobile ? 1.6 : 1.75, whiteSpace: 'pre-wrap' as const }}>{tn.answer}</div>
                        {tn.actions?.map(a => <ActionCard key={a.id} card={a} onChange={c => updateAction(active.id, i, c)} />)}
                        <MemorySavedNote items={tn.memorySaved} />
                        {tn.context_period && <div style={{ fontSize: 11, color: T.textDim, marginTop: 10 }}>{t('Dönem')}: {tn.context_period}</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, padding: isMobile ? 10 : 16 }}>
              <MediaChips attach={attach} clientId={clientKey} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${T.borderMid}`, borderRadius: 14, padding: '10px 12px' }}>
                <AttachButton attach={attach} />
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask(input)}
                  placeholder={t('Başka bir şey sor…')}
                  style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 13.5, color: T.text, background: 'transparent' }} />
                <button onClick={() => ask(input)} disabled={!input.trim()} style={{ width: 30, height: 30, borderRadius: '50%', border: 'none',
                  background: T.accent, color:'#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: input.trim() ? 'pointer' : 'default', opacity: input.trim() ? 1 : 0.5, flexShrink: 0 }}>
                  <Icon.send size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
